<template>
    <div class="vertical-bar-chart flex-row">
        <div class=" wrapper wp-8">
            <!-- Custom label -->
            <div class="bar-labels-avatars flex-column jc-center" :height="heightBarChart">
                <div class="avatars-wrapper" v-for="(id, index) in data.labels" :key="index">
                    <el-tooltip :content="avatar(id).fullname" placement="bottom-start" popper-class="horizontal-bar-chart-tool-tip">
                        <avatar  :avatar="avatar(id)" size="small"/>
                    </el-tooltip>
                </div>
            </div>
            <!-- Chart-->
            <bar-chart class="bar-chart wp-10" :height="heightBarChart" ref="barChart" :data="data" :options="options" :key="data.labels.length" @changeData="onChangeData()"></bar-chart>
        </div>
        <!-- Custom legend -->
        <div class="bar-legend wp-2 m-4">
            <div v-if="isChartForLabel"  >
                <p class="fs-3 mb-1">{{ $l10n.__("Sélectionner une activité") }}</p>
                <el-select v-model="activitySelected" class="mb-3" @change="onChangeActivitySelected">
                    <el-option v-for="activity in activities"       
                               :key="activity.value"
                               :label="activity.title"
                               :value="activity.value"></el-option>
                  </el-select>
            </div>
            <p class="semibold mt-2 mb-3 fs-3">
                <span v-if="isChartForLabel">{{ $l10n.__("Sélection des labels") }}</span>
                <span v-else>{{ $l10n.__("Sélection des activités") }}</span>
            </p>
            <el-checkbox-group v-model="checkbox" class="flex-column list-label">
                <el-checkbox v-for="(dataset, index) in data.datasets" @change="isChecked => handleFilterLegendChange(isChecked, index)" :label="index" :key="index" class="mb-3">
                    <chip-label :color="dataset.backgroundColor" :label="dataset.label" gap="small" />
                </el-checkbox>
            </el-checkbox-group>
        </div>
    </div>
</template>
<script>
    import BarChart from './HorizontalBar.js'
    import './BarChartRounded.js'
    import ChipLabel from "vcomponents/utilities/ChipLabel.vue";
    import Avatar from 'vcomponents/utilities/Avatar.vue';

    import { createNamespacedHelpers } from 'vuex';

    const report = createNamespacedHelpers('report');

    export default {
        name: 'HorizontalBarChart',
        props: {
            data : {
                type: Object,
                required: true
            }
        },
        data() {
            return {
                options: null,
                activities: [
                  { value : 'task', title : "Tâche"},
                  { value : 'event', title : "Evènement" },
                ],
                checkbox: [],
                activitySelected : 'task',
            }
        },
        created(){
            this.setOptions();
            this.populateCheckedActivities();
        },
        computed: {
            ...report.mapGetters([
                'staffs',
                'type',
            ]),
            /**
             * Calculate the height of the barchart container
             */
            heightBarChart() {
                const height = this.data.labels.length * 24 * 2;
                return height < 400 ? 400 : height;
            },
            /**
             * Return an object nedeed to avatar component
             */
            avatar() {
                return (key) => {
                    let avatar = {
                        type    : 'initials',
                        value   : ' ',
                        class   : null,
                        fullname: ' '
                    }
                    const id = key.replace('key_', '');
                    const staff = this.staffs && this.staffs.find(staff => staff.id === +id);
                    if (staff) {
                        avatar = {
                            type    : staff.avatar ? 'picture' : 'initials',
                            value   : staff.avatar ? staff.avatar : staff.firstname.charAt(0) + staff.lastname.charAt(0) || ' ',
                            class   : null,
                            fullname: `${staff.firstname || ''} ${staff.lastname || ''}`
                        }
                    }

                    return avatar;
                };
            },
            isChartForLabel() {
                return this.type === 'label';
            }
        }, 
        methods : {
            /**
             * Call the handleFilterLegendChange method each time the data changes  
             * to update the barchart correctly
             */
            onChangeData() {
                this.data.datasets.forEach((_, index) => this.handleFilterLegendChange(this.checkbox.includes(index), index));
            },
            /**
             * Manage the visibility of the bars of the graph and sort them accordingly
             */
            handleFilterLegendChange(isChecked, index) {
                const ci   = this.$refs.barChart.$data._chart;
                const meta = ci.getDatasetMeta(index);

                meta.hidden = !isChecked;
                this.sortData(ci);

                ci.update();
            },
            /**
             * Sort the graph when making activities hidden or visible
             * 
             * @param {Object} ci - Chart object
             * @return {void}
             */
            sortData(ci) {

                const {data: {datasets, labels}} = ci;

                // Get the data from the datasets
                // Then create the object to sort with data and labels (to sort avatars)
                const data         = datasets.reduce((obj, curr, i) => ({...obj, [i]:curr.data}), {});
                const dataToSorted = {...data, [Object.keys(data).length]: labels};

                // Get the hidden dataset indexes
                // Then add to the hiddenIndexes array the index of the labels array so that it is not taken into account in the sort parameters
                const hiddenIndexes  = Object.entries(datasets).filter(([k, v]) => v._meta[Object.keys(v._meta)[0]].hidden).map(([k, v]) => +k)
                const excludeIndexes = [...hiddenIndexes, Object.keys(data).length];

                // Utility function to create arrays by column
                const arrayWithColumn = (arr, colIndex) => arr.map(row => row[colIndex]);

                // Utility function to add up all visible activities
                const countVisibleActivities = arr => arr.reduce((r, v, i) => r + (excludeIndexes.includes(i) ? 0 : v), 0);

                // Creation of an array by columns that we sort by total visible activities
                const dataSortedByTotalInColumn = dataToSorted[0]
                    .map((_, colIndex) => arrayWithColumn(Object.values(dataToSorted), colIndex))
                    .sort((a, b) => countVisibleActivities(b) - countVisibleActivities(a))
                ;

                // Return column arrays to original format
                const dataSorted = dataSortedByTotalInColumn[0]?.map((_, colIndex) => arrayWithColumn(dataSortedByTotalInColumn, colIndex)) || [];

                // Update datasets with sorted data
                Object.values(dataSorted).forEach((sorted, i) => i < Object.keys(data).length
                    ? ci.data.datasets[i].data = sorted
                    : ci.data.labels = sorted
                )
            },
            /**
             * Set needed options for vertical bar chart
             */
            setOptions() {
                this.options = {
                    indexAxis: 'x',
                    responsive: true, 
                    maintainAspectRatio: false,
                    defaultFontFamily: "'Proxima Nova', 'Open Sans', sans-serif",
                    defaultFontSize: 14,
                    defaultFontStyle: "normal",
                    defaultFontColor: "#5C5C65",
                    isRoundedBar: true,

                    scales: {
                        xAxes: [{
                            id: 'top-x-axis',
                            position: 'top',
                            stacked: true,
                            gridLines: {
                                display: true,
                                color: '#D8D8E6',
                                borderDash: [5,5],
                                drawTicks: false,
                            },
                            ticks: {
                                display: true,
                                fontFamily: "'Proxima Nova', 'Open Sans', sans-serif",
                                fontSize: 14,
                                fontColor: "#5C5C65",
                                padding: 12,
                                beginAtZero: true,
                                callback: function(value, index, values) {
                                    if (index !== 0 && Math.floor(value) === value) return value;
                                },
                            }
                        }, {
                            id: 'bottom-x-axis',
                            position: 'bottom',
                            gridLines: { display: false },
                            ticks: { display: false }
                        }],
                        yAxes: [{
                            id: 'left-y-axis',
                            position: 'left',
                            stacked: true,
                            gridLines: { display: false },
                            ticks: { display: false },
                        }, {
                            id: 'right-y-axis',
                            position: 'right',
                            gridLines: { display: false },
                            ticks: { display: false }
                        }],
                    },
                    legend: {
                        display: false,
                    },
                    tooltips: {
                        mode: 'nearest',
                        callbacks: {
                            title: (tooltipItems, data) => {
                                const key   = tooltipItems[0].yLabel;
                                const title = this.avatar(key);
                                return title ? title.fullname : '';
                            },
                            label: (tooltipItem, data) => {
                                return `${data.datasets[tooltipItem.datasetIndex].label || ''} : ${Math.round(tooltipItem.xLabel * 100) / 100}`;
                            }
                        }
                    },
                    animation: {
                        duration: 400
                    }
                };
            },
            onChangeActivitySelected() {
                this.$store.dispatch('report/updateActivitySelected', this.activitySelected);
            },
            populateCheckedActivities() {
                this.checkbox = [];
                this.data.datasets.forEach((_, index) => {
                    this.checkbox.push(index);
                })
            },
        },
        watch: {
          data: function () {
              this.populateCheckedActivities();
          }
        },
        components: {
            BarChart,
            ChipLabel,
            Avatar
        },
    }

</script>

<style lang="scss" scoped>

    @import '~@sellsy/styleguide/src/_variables';
    @import '~@sellsy/styleguide/src/_mixins';

    .list-label {
      max-height: calc(99vh - 500px);
    }
    
    .vertical-bar-chart {
        max-height: calc(99vh - 330px);
        // bar-chart
        .wrapper {
            display: grid;
            grid-template-columns: 24px 1fr;
            overflow-y: auto;
            &::-webkit-scrollbar {
                background: $white;
                width: 6px;
                display: block;
            }
            &::-webkit-scrollbar-thumb {
                background: $gray-60;
                border-radius: 6px;
            }
            // bar-labels-avatars
            .bar-labels-avatars {
                padding-left: 17px;
                margin-right: -33px;
                margin-top: 34px;
                margin-bottom: 10px;
                justify-content: space-around;
                .avatars-wrapper {
                    width: 24px;
                }
            }
        }
    }
</style>

<style>
    .horizontal-bar-chart-tool-tip{
        transform: translateX(-8.5%);
    }
</style>